import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/tournament-results`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

export const getVolunteerTournamentsForResults = async () => {
  const res = await authFetch(`${BASE}/tournaments`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load tournaments");
  return data;
};

export const getVolunteerTournamentResultTypes = async () => {
  const res = await authFetch(`${BASE}/result-types`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load result types");
  return data;
};

export const getVolunteerTournamentResultCandidates = async (params = {}) => {
  const q = new URLSearchParams();
  if (params.tournament_id) q.set("tournament_id", String(params.tournament_id));
  if (params.student_search) q.set("student_search", params.student_search);
  if (params.filter_column) q.set("filter_column", String(params.filter_column));
  if (params.registration_verified !== undefined && params.registration_verified !== "") {
    q.set("registration_verified", String(params.registration_verified));
  }
  if (params.has_results !== undefined && params.has_results !== "") {
    q.set("has_results", String(params.has_results));
  }
  if (params.registered_from) q.set("registered_from", params.registered_from);
  if (params.registered_to) q.set("registered_to", params.registered_to);
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  const res = await authFetch(`${BASE}/participants${qs ? `?${qs}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Failed to load participants");
  return data;
};

export const postVolunteerTournamentResultsUpload = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/upload`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Upload failed");
  return data;
};

function parseContentDispositionFilename(header) {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export class TournamentCertificatesError extends Error {
  constructor(message, blocked = []) {
    super(message);
    this.name = "TournamentCertificatesError";
    this.blocked = blocked;
  }
}

/** Generate PDF (one student) or ZIP (bulk); triggers browser download. */
export const downloadTournamentCertificates = async (body, csrfToken) => {
  const res = await authFetch(`${BASE}/certificates/generate`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(
      csrfToken ? { "X-CSRF-Token": csrfToken, Accept: "application/pdf, application/zip" } : { Accept: "application/pdf, application/zip" },
    ),
    body: JSON.stringify(body),
  });

  const contentType = res.headers.get("Content-Type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const data = await parseJson(res);
      throw new TournamentCertificatesError(
        data.message || "Certificate download failed",
        data?.data?.blocked || [],
      );
    }
    const text = await res.text();
    throw new TournamentCertificatesError(text || "Certificate download failed");
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ||
    (contentType.includes("zip") ? "tournament-certificates.zip" : "certificate.pdf");
  triggerBrowserDownload(blob, filename);
  return { filename, count: body?.student_ids?.length ?? 0 };
};
